import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { StatusBadge, ASSET_STATUSES, ASSET_CATEGORIES } from "@/lib/status";
import PhotoUploader from "@/components/PhotoUploader";
import QRScanner from "@/components/QRScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Search, Boxes, ImageOff, ArrowRight, ScanLine, Tags, FileDown } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "", make: "", model: "", serial: "", location: "", status: "Operational", purchase_date: "", warranty_expiry: "", value: "", notes: "", photos: [] };

export default function Assets() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const patFilter = searchParams.get("pat");
  const [assets, setAssets] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.listAssets({ q: q || undefined, status }).then(setAssets).catch(() => setAssets([]));
  }, [q, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const handleScan = (text) => {
    setScanOpen(false);
    const m = /assets\/([0-9a-f-]{8,})/i.exec(text || "");
    const target = m ? m[1] : (text || "").trim();
    if (target) nav(`/assets/${target}`);
    else toast.error("Not a valid asset QR");
  };

  const visible = (assets || []).filter((a) =>
    !patFilter ? true : patFilter === "overdue" ? a.pat_status === "PAT overdue" : a.pat_status === "PAT due soon"
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.createAsset(form);
      toast.success("Asset added");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Could not add asset"); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Inventory</span>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Assets</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="scan-qr-btn" onClick={() => setScanOpen(true)} variant="outline" className="gap-2 tap-scale"><ScanLine className="h-4 w-4" /> <span className="hidden sm:inline">Scan</span></Button>
          <Button data-testid="assets-pdf-btn" onClick={() => nav("/reports/assets")} variant="outline" className="gap-2 tap-scale"><FileDown className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span></Button>
          <Button data-testid="print-tags-nav-btn" onClick={() => nav("/assets/tags")} variant="outline" className="gap-2 tap-scale"><Tags className="h-4 w-4" /> <span className="hidden sm:inline">Tags</span></Button>
          <Button data-testid="new-asset-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
            <Plus className="h-4 w-4" /> New asset
          </Button>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="asset-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, serial, location…" className="bg-secondary/40 pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="asset-status-filter" className="w-full bg-secondary/40 sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {ASSET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {patFilter && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm">
          <span>Showing assets with <b>PAT {patFilter}</b></span>
          <button onClick={() => nav("/assets")} className="text-primary tap-scale">Clear</button>
        </div>
      )}

      {assets === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center">
          <Boxes className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No assets found. Add one, or generate assets from a site survey.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((a) => (
            <button key={a.id} data-testid={`asset-card-${a.id}`} onClick={() => nav(`/assets/${a.id}`)} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-left card-hover">
              <div className="relative aspect-video w-full overflow-hidden bg-secondary/40">
                {a.photos?.[0] ? (
                  <img src={fileUrl(a.photos[0].storage_path)} alt={a.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground"><ImageOff className="h-8 w-8" /></div>
                )}
                <span className="absolute left-2 top-2 rounded-md bg-background/85 px-1.5 py-0.5 font-mono text-[10px] font-bold text-foreground">{a.asset_code}</span>
                <span className="absolute right-2 top-2"><StatusBadge value={a.status} /></span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="text-sm font-semibold leading-tight text-foreground">{a.name}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">{[a.make, a.model].filter(Boolean).join(" · ") || a.category || "—"}</p>
                <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{a.location || "No location"}</span>
                  {a.pat_status && a.pat_status !== "Untested" ? <StatusBadge value={a.pat_status} /> : <ArrowRight className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New asset</DialogTitle>
            <DialogDescription>Register a piece of equipment in the inventory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs text-muted-foreground">Name *</Label>
              <Input data-testid="asset-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-secondary/40" placeholder="e.g. ETC Source Four LED" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-xs text-muted-foreground">Category</Label>
                <Select value={form.category || undefined} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="asset-category" className="bg-secondary/40"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{ASSET_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="asset-status" className="bg-secondary/40"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Make</Label><Input data-testid="asset-make" value={form.make} onChange={(e) => set("make", e.target.value)} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Model</Label><Input data-testid="asset-model" value={form.model} onChange={(e) => set("model", e.target.value)} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Serial / venue ID</Label><Input data-testid="asset-serial" value={form.serial} onChange={(e) => set("serial", e.target.value)} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Location</Label><Input data-testid="asset-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Purchase date</Label><Input type="date" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Warranty expiry</Label><Input type="date" value={form.warranty_expiry} onChange={(e) => set("warranty_expiry", e.target.value)} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Value</Label><Input value={form.value} onChange={(e) => set("value", e.target.value)} className="bg-secondary/40" placeholder="£" /></div>
            </div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-secondary/40" /></div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Photos</Label><PhotoUploader testid="asset-photo" photos={form.photos} onChange={(p) => set("photos", p)} /></div>
            <Button data-testid="create-asset-confirm" onClick={create} disabled={busy} className="w-full gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add asset
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <QRScanner open={scanOpen} onClose={() => setScanOpen(false)} onResult={handleScan} />
    </div>
  );
}
